import { useEffect, useState } from "react";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ShareToFacebook } from "@/components/admin/ShareToFacebook";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

export function MerchandiseManager() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (!site) return <p className="text-[#555555]">Loading…</p>;

  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));
  const items = site.merchandise || [];
  const add = () => set("merchandise", [...items, { id: crypto.randomUUID(), image: "", caption: "" }]);
  const upd = (i, k, v) => set("merchandise", items.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const rm = (i) => set("merchandise", items.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...items];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    set("merchandise", arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Merchandise saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="merchandise-manager">
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="save-merch-top-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save merchandise"}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1">Intro text</label>
          <textarea className={input} rows={3} value={site.merch_intro || ""} onChange={(e) => set("merch_intro", e.target.value)} data-testid="merch-intro-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1">Order / contact email</label>
          <input className={input} value={site.merch_email || ""} onChange={(e) => set("merch_email", e.target.value)} placeholder="quintebca@gmail.com" data-testid="merch-email-input" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-lg">Merchandise images</h3>
          <p className="text-xs text-[#555555]">Drag the handle to reorder. Order is saved with the page.</p>
        </div>
        <button onClick={add} data-testid="add-merch-btn" className="inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#c41320]">
          <Plus className="h-4 w-4" /> Add image
        </button>
      </div>

      <div className="space-y-3">
        {items.map((m, i) => (
          <div
            key={m.id || i}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={() => onDrop(i)}
            data-testid={`merch-row-${i}`}
            className={`flex items-start gap-3 bg-white rounded-lg border p-4 transition-all ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#1E63C4] ring-2 ring-[#1E63C4]/30" : "border-[#E5E7EB]"
            } ${dragIdx === i ? "opacity-50" : ""}`}
          >
            <button
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`merch-drag-${i}`}
              className="mt-1 cursor-grab active:cursor-grabbing text-[#999999] hover:text-[#1E63C4]"
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <div className="flex-1 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-[#555555]">Image {i + 1}</span>
                <button onClick={() => rm(i)} className="text-red-500" data-testid={`remove-merch-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <ImageUpload label="Merch image" value={m.image} onChange={(v) => upd(i, "image", v)} testid={`merch-image-${i}`} />
              <input className={input} placeholder="Caption (optional)" value={m.caption} onChange={(e) => upd(i, "caption", e.target.value)} data-testid={`merch-caption-${i}`} />
              <ShareToFacebook image={m.image} defaultCaption={m.caption || ""} source="merch" testid={`fb-merch-${i}`} />
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={busy} data-testid="save-merch-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
        <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save merchandise"}
      </button>
    </div>
  );
}
